<script setup lang="ts">
    import { __ } from '@/js/stores/expressions';
    import { computed, watch } from 'vue';
    import EchoButton from '../../EchoButton.vue';
    import EchoLabel from '../../EchoLabel.vue';
    import UserAvatar from '../../UserAvatar.vue';
    import LoadingDots from '../../LoadingDots.vue';
    import {
        useGetRoleSharesForDashboard,
        useGetUserSharesForDashboard,
    } from '@/js/services/api/shareDashboard/shareDashboardQueries';
    import { AccessLevel } from '@/js/services/api/shareDashboard/types';
    import { useSharedDashboardStore } from '@/js/stores/sharedDashboard';
    import { useDashboardStore } from '@/js/stores/dashboard';
    import { dateToString } from '@/js/utils/dateUtils';
    import { usePermissions } from '@/js/composables/usePermissions';
    import { PermissionArea, DashboardModulePermissionFlag } from '@/js/consts/permissions';
    import { ChevronLeftIcon, EyeIcon, UsersIcon } from '@lucide/vue';

    const { hasPermission } = usePermissions();

    const canShareWithUsers = hasPermission(
        PermissionArea.DashboardModule,
        DashboardModulePermissionFlag.ShareWithUsers
    );

    const canShareWithRoles = hasPermission(
        PermissionArea.DashboardModule,
        DashboardModulePermissionFlag.ShareWithRoles
    );

    const props = defineProps<{
        dashboardId: string;
    }>();

    const emit = defineEmits<{
        (e: 'close'): void;
        (e: 'navigate-users'): void;
        (e: 'navigate-roles'): void;
        (e: 'navigate-links'): void;
    }>();

    const dashboard = useDashboardStore();
    const selectedDashboard = computed(() => dashboard.selectedDashboard);
    const sharedDashboard = useSharedDashboardStore();

    const { data: sharedUsersResponse, isLoading: isLoadingUsers } = useGetUserSharesForDashboard(
        props.dashboardId,
        canShareWithUsers
    );

    const { data: sharedRolesResponse, isLoading: isLoadingRoles } = useGetRoleSharesForDashboard(
        props.dashboardId,
        canShareWithRoles
    );

    const sharedUsers = computed(() => sharedUsersResponse.value ?? []);
    const sharedRoles = computed(() => sharedRolesResponse.value ?? []);
    const sharedLinks = computed(() => sharedDashboard.sharedDashboardLinks ?? []);

    watch(
        () => props.dashboardId,
        async (newId) => {
            if (newId) {
                await sharedDashboard.getSharedDashboardLinksByDashboard(newId);
            }
        },
        { immediate: true }
    );
</script>

<template>
    <div class="flex-grow flex flex-col -mx-8 -mb-8">
        <div class="mb-6 px-8">
            <div class="mb-4">
                <EchoButton variant="tertiary" @click="emit('close')">
                    <template #icon-left><ChevronLeftIcon /></template>
                    {{ __('back') }}
                </EchoButton>
            </div>

            <div class="mb-6">
                <h2 class="heading-md">
                    {{ __('all-shares') }}
                </h2>
                <p class="text-body">
                    {{ selectedDashboard?.title }}
                </p>
            </div>

            <template
                v-if="sharedUsers.length > 0 || sharedRoles.length > 0 || sharedLinks.length > 0"
            >
                <h3 class="text-body-bold mb-1">{{ __('shared-via') }}:</h3>
                <div class="flex gap-2">
                    <EchoLabel v-if="canShareWithUsers && sharedUsers.length > 0" variant="light">
                        {{ __('shared-via-users', { count: sharedUsers.length.toString() }) }}
                    </EchoLabel>
                    <EchoLabel v-if="canShareWithRoles && sharedRoles.length > 0" variant="light">
                        {{ __('shared-via-roles', { count: sharedRoles.length.toString() }) }}
                    </EchoLabel>
                    <EchoLabel v-if="sharedLinks.length > 0" variant="light">
                        {{ __('shared-via-links', { count: sharedLinks.length.toString() }) }}
                    </EchoLabel>
                </div>
            </template>
        </div>

        <div
            v-if="(canShareWithUsers && isLoadingUsers) || (canShareWithRoles && isLoadingRoles)"
            class="py-10"
        >
            <LoadingDots />
        </div>

        <div v-else class="lg:h-0 flex-grow overflow-auto custom-scrollbar p-8">
            <!-- Users section -->
            <section v-if="canShareWithUsers" class="mb-8">
                <div class="flex items-center justify-between border-b border-gray-light pb-2 mb-1">
                    <h3 class="heading-md-light">
                        {{ __('users') }}
                    </h3>
                    <EchoButton variant="secondary" size="small" @click="emit('navigate-users')">
                        <template #icon-left><UsersIcon /></template>
                        {{ __('manage-sharing') }}
                    </EchoButton>
                </div>

                <template v-if="sharedUsers.length > 0">
                    <div
                        class="grid grid-cols-[1fr_auto] text-caption-bold text-gray-dark px-3 py-2"
                    >
                        <span>{{ __('shared-with') }}</span>
                        <span>{{ __('access') }}</span>
                    </div>

                    <div>
                        <div
                            v-for="user in sharedUsers"
                            :key="'user-' + user.id"
                            class="grid grid-cols-[1fr_auto] items-center px-3 py-2 odd:bg-gray-lightest"
                        >
                            <div class="flex items-center">
                                <UserAvatar :label="user.name || user.email" size="md" />
                                <div class="pl-3">
                                    <strong class="block text-caption-bold">{{ user.name }}</strong>
                                    <span class="block text-caption-bold">
                                        {{ user.role?.name }}
                                    </span>
                                    <span class="block text-caption text-gray-dark">
                                        {{ user.email }}
                                    </span>
                                </div>
                            </div>
                            <div class="text-caption">
                                {{
                                    user.accessLevel === AccessLevel.Owner
                                        ? __('owner')
                                        : __('can-view')
                                }}
                            </div>
                        </div>
                    </div>
                </template>
            </section>

            <!-- Roles section -->
            <section v-if="canShareWithRoles" class="mb-8">
                <div class="flex items-center justify-between border-b border-gray-light pb-2 mb-1">
                    <h3 class="heading-md-light">
                        {{ __('roles') }}
                    </h3>
                    <EchoButton variant="secondary" size="small" @click="emit('navigate-roles')">
                        <template #icon-left><UsersIcon /></template>
                        {{ __('manage-sharing') }}
                    </EchoButton>
                </div>

                <template v-if="sharedRoles.length > 0">
                    <div
                        class="grid grid-cols-[1fr_auto] text-caption-bold text-gray-dark px-3 py-2"
                    >
                        <span>{{ __('shared-with') }}</span>
                        <span>{{ __('access') }}</span>
                    </div>

                    <div>
                        <div
                            v-for="role in sharedRoles"
                            :key="'role-' + role.id"
                            class="grid grid-cols-[1fr_auto] items-center px-3 py-2 odd:bg-gray-lightest"
                        >
                            <strong class="block text-caption-bold">{{ role.name }}</strong>
                            <span class="block text-caption">{{ __('can-view') }}</span>
                        </div>
                    </div>
                </template>
            </section>

            <!-- Links section -->
            <section>
                <div class="flex items-center justify-between border-b border-gray-light pb-2">
                    <h3 class="heading-md-light">
                        {{ __('links') }}
                    </h3>
                    <EchoButton variant="secondary" size="small" @click="emit('navigate-links')">
                        <template #icon-left><UsersIcon /></template>
                        {{ __('manage-sharing') }}
                    </EchoButton>
                </div>

                <template v-if="sharedLinks.length > 0">
                    <div
                        class="hidden sm:grid grid-cols-[1fr_auto] text-caption-bold text-gray-dark px-3 py-2"
                    >
                        <span>{{ __('link') }}</span>
                        <span>{{ __('access') }}</span>
                    </div>

                    <div>
                        <div
                            v-for="link in sharedLinks"
                            :key="'link-' + link.id"
                            class="sm:grid sm:grid-cols-[1fr_auto] items-center px-3 py-2 odd:bg-gray-lightest"
                        >
                            <div>
                                <span class="block text-caption-bold">
                                    {{ dateToString(link.expirationDate, null, false) }}
                                </span>
                                <span class="block text-caption text-gray-dark truncate max-w-xs">
                                    {{ link.uri }}
                                </span>
                            </div>
                            <div class="flex mt-3 sm:m-0 text-caption">
                                <span class="sm:hidden mr-1">
                                    <EyeIcon class="size-4" />
                                </span>

                                <template v-if="link.isPasswordProtected">
                                    {{ __('password-protected') }}
                                </template>
                                <template v-else>
                                    {{ __('anyone-with-link') }}
                                </template>
                            </div>
                        </div>
                    </div>
                </template>
            </section>
        </div>
    </div>
</template>
